<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex-grow-0 w-full h-full min-h-0 app-wrapper"
    :class="{ 'app-rtl--wrapper': isRTLView }"
    :dir="isRTLView ? 'rtl' : 'ltr'"
  >
    <system-banner />
    <app-update-banner />
    <template v-if="currentAccountId">
      <pending-email-verification-banner />
      <whats-new-banner />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <otello-snackbar-box />
    <network-notification />
    <drag-drop-notification
      :visible="notificationVisible"
      :message="notificationMessage"
      :type="notificationType"
    />
    <email-sending-indicator />
  </div>
  <loading-state v-else />
</template>

<script>
import AppUpdateBanner from 'dashboard/components/app/AppUpdateBanner.vue';
import { mapGetters } from 'vuex';
import router from '../dashboard/routes';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import WhatsNewBanner from './components/app/WhatsNewBanner.vue';
import SystemBanner from './components/app/SystemBanner.vue';
import OtelloSnackbarBox from './components/SnackbarContainer.vue';
import DragDropNotification from './components/ui/DragDropNotification.vue';
import EmailSendingIndicator from './components/widgets/EmailSendingIndicator.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import { setColorTheme } from './helper/themeHelper';
import vueActionCable from './helper/actionCable';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { loadLocaleMessages } from './i18n';
import {
  addSafeEventListener,
  removeSafeEventListener,
} from 'dashboard/helper/domEvents';
import { getOtelloConfig } from 'dashboard/helper/browserNavigation';
import {
  isMediaQueryMatching,
  safeSetMediaQueryChangeHandler,
} from 'dashboard/helper/browserRuntime';

const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';

export default {
  name: 'App',

  components: {
    AppUpdateBanner,
    LoadingState,
    NetworkNotification,
    OtelloSnackbarBox,
    PendingEmailVerificationBanner,
    WhatsNewBanner,
    SystemBanner,
    DragDropNotification,
    EmailSendingIndicator,
  },

  mixins: [rtlMixin],

  data() {
    return {
      accountInitializationVersion: 0,
      actionCableConnector: null,
      reconnectService: null,
      theme: null,
      systemThemeDark: false,
    };
  },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      currentAccountId: 'getCurrentAccountId',
      notificationVisible: 'notifications/getVisible',
      notificationMessage: 'notifications/getMessage',
      notificationType: 'notifications/getType',
    }),
  },

  watch: {
    currentAccountId() {
      if (this.currentAccountId) {
        this.initializeAccount();
      }
    },
    currentUser() {
      this.initializeColorTheme();
    },
  },
  async mounted() {
    await this.checkLogin();
    // Handle direct label assignment events at the application level
    addSafeEventListener(
      document,
      'conversation-label-drop',
      this.handleGlobalLabelDrop
    );
  },
  beforeUnmount() {
    this.accountInitializationVersion += 1;
    if (this.actionCableConnector) {
      this.actionCableConnector.disconnect();
      this.actionCableConnector = null;
    }
    if (this.reconnectService) {
      this.reconnectService.disconnect();
      this.reconnectService = null;
    }
    removeSafeEventListener(
      document,
      'conversation-label-drop',
      this.handleGlobalLabelDrop
    );
  },
  methods: {
    getThemeUISettings() {
      return this.currentUser?.id ? this.currentUser.ui_settings || {} : null;
    },
    initializeColorTheme() {
      setColorTheme(
        isMediaQueryMatching(DARK_MODE_QUERY),
        this.getThemeUISettings()
      );
    },
    listenToThemeChanges() {
      safeSetMediaQueryChangeHandler(DARK_MODE_QUERY, (event) =>
        setColorTheme(event.matches, this.getThemeUISettings())
      );
    },
    async setLocale(locale) {
      await loadLocaleMessages(this.$root.$i18n, locale);
    },
    async checkLogin() {
      this.initializeColorTheme();
      this.listenToThemeChanges();
      await this.setLocale(getOtelloConfig().selectedLocale);
    },
    async initializeAccount() {
      const initializationVersion = this.accountInitializationVersion + 1;
      this.accountInitializationVersion = initializationVersion;
      const accountId = this.currentAccountId;

      if (this.actionCableConnector) {
        this.actionCableConnector.disconnect({
          preservePendingReconnect: true,
        });
        this.actionCableConnector = null;
      }
      if (this.reconnectService) {
        this.reconnectService.disconnect();
        this.reconnectService = null;
      }

      await this.$store.dispatch('accounts/get');
      if (
        initializationVersion !== this.accountInitializationVersion ||
        String(this.currentAccountId) !== String(accountId)
      ) {
        return;
      }
      // Hotel dell'account aperto (Automazioni, strumenti e socket della barra
      // destra, mittente degli inoltri): si carica qui, per tutti, a ogni
      // ingresso nell'account. Non blocca l'avvio.
      this.$store.dispatch('hotels/fetchHotelForAccount', accountId);
      await this.$store.dispatch('setActiveAccount', {
        accountId,
      });
      const { locale } = this.getAccount(accountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      await this.setLocale(locale);
      if (
        initializationVersion !== this.accountInitializationVersion ||
        String(this.currentAccountId) !== String(accountId)
      ) {
        return;
      }
      this.updateRTLDirectionView(locale);
      this.actionCableConnector = vueActionCable.init(pubsubToken);
      this.reconnectService = new ReconnectService(this.$store, router);
    },
    handleGlobalLabelDrop(event) {
      const { conversationId, labelId } = event.detail;

      if (conversationId && labelId) {
        // Tentativo di assegnazione diretta
        this.$store
          .dispatch('conversationLabels/update', {
            conversationId: parseInt(conversationId, 10),
            labels: [parseInt(labelId, 10)],
          })
          .then(() => {
            this.$store.dispatch('fetchAllConversations');
            this.$store.dispatch('notifications/show', {
              type: 'success',
              message: this.$t('CONVERSATION_LABELS.ADDED'),
            });
          })
          .catch(() => {
            this.$store.dispatch('notifications/show', {
              type: 'error',
              message: this.$t('CONVERSATION.LABELS.ASSIGNMENT_FAILED'),
            });
          });
      }
    },
  },
};
</script>
